import { ArticleModel, IArticle, IReplacingProduct } from '../models/article.model';
import { IBomModel, IEcommerceBomItem, IShoppingCart } from '../models/bom.model';
import { IProductAccessories } from '../../enclosure/models/product-accessories.model';
import { IProduct, ProductStatus } from '../../shared/models/product.model';
import { IDevice } from '../../shared/models/device.model';
import { NOT_BILLABLE } from '../../enclosure/models/devices.constants';
import { SortingOrder } from '../../shared/models/table-sort.model';
import { ICountProjectDevices } from '../../project-layer/models/count-project-devices.model';
import { shallowCloneObject } from './prisma-xs.helper';
import { WholesalerUnableToTransferDevicesExistPipe } from '../../ui/pipes/wholesaler-unable-to-transfer-devices-exist.pipe';
import { PreWiringDevicesIncludedPipe } from '../../ui/pipes/pre-wiring-devices-included.pipe';
import { NumberOfBomPositionsPipe } from '../../ui/pipes/number-of-bom-positions.pipe';
import { WholesalerUnableToTransferProjectDevicesExistPipe } from '../../ui/pipes/wholesaler-unable-to-transfer-project-devices-exist.pipe';
import { PreWiringProjectDevicesIncludedPipe } from '../../ui/pipes/pre-wiring-project-devices-included.pipe';
import { NumberOfProjectBomPositionsPipe } from '../../ui/pipes/number-of-project-bom-positions.pipe';
import { IBomAnalytics } from '../../shared/models/analytics';
import { CountTotalPriceBomPipe } from '../../ui/pipes/count-total-price-bom.pipe';

export const isArticleExistsInBOMModel = (bomArticles: IBomModel[], article: IArticle) => {
  return bomArticles.findIndex(bomArticle => {
    return bomArticle?.article?.ref === article?.ref && bomArticle?.article?.eNumber === article?.eNumber
      && bomArticle?.article?.productId === article?.productId;
  });
};

// eslint-disable-next-line max-len
export const mapGeneralProductToArticle = (product: IProduct, id, deliverToCustomer, deliverToPanelBuilder, rowNumber, columnNumber, isCustomDevice, encId?) => {
  const article = new ArticleModel();
  article.id = id;
  article.productId = product.productId;
  article.productType = product.type;
  article.eNumber = product.enumber;
  article.ref = product.reference;
  article.description = product.pDescription;
  article.price = product.price;
  article.deliverToCustomer = deliverToCustomer;
  article.deliverToPanelBuilder = deliverToPanelBuilder;
  article.width = product.width;
  article.rowNumber = rowNumber;
  article.columnNumber = columnNumber;
  article.isCustomDevice = isCustomDevice;
  article.status = product.status;
  article.productTypeId = product.productTypeId;
  article.enclosureId = encId ?? undefined;
  article.productCharacteristicsLink = product?.presentationRules?.deviceProductCharacteristicsLink
  article.productInfoLink = product?.presentationRules.productInfoLink
  article.canTransferToWholesaler = product.bomRules?.canTransferToWholesaler;
  article.isNxsDummy = product?.productCharacteristicsRules?.isCompactNsxDummy;
  article.replacingProduct = product?.replacingProduct ? mapReplacingProductToArticle(product?.replacingProduct) : null;
  article.priceBeforeDiscount = product?.bomRules?.priceBeforeDiscount;

  return article;
};

export const mapReplacingProductToArticle = (product: IProduct): IReplacingProduct => {
  return {
    eNumber: product?.enumber,
    ref: product?.reference,
    description: product?.pDescription,
    price: product?.price,
    productCharacteristicsLink: product?.presentationRules?.deviceProductCharacteristicsLink,
    stockLevel: 0
  };
};


export const mapProductToBOM = (articles: IArticle[]): IBomModel[] => {
  const bomArticles: IBomModel[] = [];

  articles.forEach(article => {

    const indexInBOMModel = isArticleExistsInBOMModel(bomArticles, article);
    if (indexInBOMModel >= 0) {
      bomArticles[indexInBOMModel].quantity += 1;
      prioritisePrewiringDeliveryOption(bomArticles[indexInBOMModel], {
        quantity: 1,
        article
      });
    } else {
      bomArticles.push({
        quantity: 1,
        article
      });
    }
  });

  return bomArticles;
};

export const mapProductToProjectBOM = (articles: IArticle[]): IBomModel[] => {
  const bomArticles: IBomModel[] = [];

  articles?.forEach(article => {
    const index = bomArticles.findIndex(a => a.article.id === article.id);
    if(index > -1) {
      bomArticles[index].quantity += 1;
    } else {
      bomArticles.push({
        quantity: 1,
        article
      });
    }
  });

  return bomArticles;
};

export const isStatusActive = (status: ProductStatus): boolean => {
  return status === ProductStatus.ACTIVE;
}

export const sumOfProductsFromBOM = (bomModels: IBomModel[], apartmentsNumber?: number) => {
  return bomModels
    .filter(model => isStatusActive(model?.article?.status))
    .reduce((acc: number, bomModel: IBomModel) => {
      return acc += bomModel.quantity * bomModel.article.price * (apartmentsNumber > 0 ? apartmentsNumber : 1);
    }, 0);
};

// eslint-disable-next-line max-len
const getUpdatedSlicedArray = (accessoryArray: IBomModel[] | IProductAccessories[], index: number, quantity: number) => {

  const accessoryElement = {
    ...accessoryArray[index],
    quantity
  };

  return [
    ...accessoryArray.slice(0, index),
    accessoryElement,
    ...accessoryArray.slice(index + 1)
  ];
};


// eslint-disable-next-line max-len
export function updateQuantityOnBomModel(bomModelsAccessories: IBomModel[], quantity: number, additionalBOMEntryId: number) {
  const accessoryBomModelIndex = bomModelsAccessories.findIndex(acc => acc.article.id === additionalBOMEntryId);

  return getUpdatedSlicedArray(bomModelsAccessories, accessoryBomModelIndex, quantity);
}

// eslint-disable-next-line max-len
export function updateQuantityOnAccessoryModel(productAccessories: IProductAccessories[], quantity: number, additionalBOMEntryId: number) {
  const accessoryProductIndex = productAccessories.findIndex(acc => acc.additionalBOMEntryId === additionalBOMEntryId);

  return getUpdatedSlicedArray(productAccessories, accessoryProductIndex, quantity);
}

// eslint-disable-next-line max-len
export function updateDeliveryKindOnBomModel(bomModelsAccessories: IBomModel[], deliverToCustomer: boolean, quantity: number, additionalBOMEntryId: number) {
  const accessoryBomModelIndex = bomModelsAccessories.findIndex(acc => acc.article.id === additionalBOMEntryId);

  const accessoryElement: IBomModel = {
    article: { ...bomModelsAccessories[accessoryBomModelIndex].article, deliverToCustomer, deliverToPanelBuilder: !deliverToCustomer },
    quantity
  };

  return [
    ...bomModelsAccessories.slice(0, accessoryBomModelIndex),
    accessoryElement,
    ...bomModelsAccessories.slice(accessoryBomModelIndex + 1)
  ];
}

export const checkIfProductIsValidForCopying = (bomModel: IBomModel, parameter: string): boolean => {
  return !!parameter
    && bomModel?.article[parameter] !== null
    && bomModel?.article[parameter] !== 0
    && isStatusActive(bomModel?.article?.status);
}

export const checkForAnyInactiveProducts = (bomModels: IBomModel[]) => {
  const index = bomModels?.findIndex(model => model.article.status !== ProductStatus.ACTIVE)
  return (index > -1);
}

export const shouldProductBeRemovedFromBOM = (device: Partial<IDevice>) => {
  return device.deviceOptions === NOT_BILLABLE;
}

export const filterOutDevicesThatShouldNotBePlacedInBOM = (devices: IDevice[]) => {
  return devices.filter(device => !shouldProductBeRemovedFromBOM(device))
}

export const toggleDisplay = (e: HTMLElement) => {
  e.classList.toggle('d-none');
}

export const addItem = (model: IBomModel, selectionArray: number[]): void => {
  if(!isStatusActive(model?.article?.status)) {
    return;
  }

  selectionArray?.push(model.article.id);
}

export const updateBOMModelsAfterDelete = (additionalBOMEntryId: number,
                                           bomModels: IBomModel[]): IBomModel[] => {
  if(!additionalBOMEntryId || !bomModels) {
    return;
  }
  const accessoryBomModelIndex = bomModels.findIndex(acc => acc.article.id === additionalBOMEntryId);
  bomModels = [
    ...bomModels.slice(0,accessoryBomModelIndex),
    ...bomModels.slice(accessoryBomModelIndex+1)
  ];
  return bomModels;
};

export const mapProducts = (result) => {
  return result.map((item) => {
    return {
      ...item,
      enumber: item.enumber.toString()
    };
  });
};

export const createNewArticles = (devices: IDevice[]) => {
  return devices.map(device => mapGeneralProductToArticle(
    device.product,
    device.id,
    device.deliverToCustomer,
    device.deliverToPanelBuilder,
    device.rowNumber,
    device.columnNumber,
    device.isCustomDevice,
    device.enclosureId));
};

export const getNextSortSequence = (currentValue: SortingOrder) => {
  switch (currentValue) {
    case SortingOrder.ASCENDING: return SortingOrder.DESCENDING;
    case SortingOrder.DESCENDING: return SortingOrder.NONE;
    case SortingOrder.NONE: return SortingOrder.ASCENDING;
  }
}

export const sumOfQuantitiesOfBomModels = (bomModels: IBomModel[], selectedDevices: number[], apartmentsNumber?: number) => {
  return bomModels
    .filter(model => isStatusActive(model?.article?.status))
    .reduce((acc: number, bomModel: IBomModel) => {
      return acc += selectedDevices.includes(bomModel.article.id) ?
        bomModel.quantity * (apartmentsNumber > 0 ? apartmentsNumber : 1) : 0;
    }, 0);
};

export const filterSelectedItemsForCSV = (selectedItems: number[], model: IBomModel[], selectedEnclosures: number[]): number[] => {
  model = model.filter(m => selectedItems.indexOf(m.article.id) > -1);
  return filterBySelectedEnclosure(selectedEnclosures, model)
    .map(m => m.article.id);
}

export const getCountOfTotalDevicesProjectBOM = (bomModels: IBomModel[],
                                                 bomModelsAccessories: IBomModel[],
                                                 bomModelsPC: IBomModel[],
                                                 selectedEnclosures: number[]) => {
  if(!selectedEnclosures) {
    return;
  }
  selectedEnclosures = [0, ...selectedEnclosures];
  return {
    numberOfDevices: getTotalCountOfDevices(filterModelsBasedOnSelectedEnclosures(bomModels, selectedEnclosures)),
    numberOfAccessories: getTotalCountOfDevices(filterModelsBasedOnSelectedEnclosures(bomModelsAccessories, selectedEnclosures)),
    numberOfComponents: getTotalCountOfDevices(filterBySelectedEnclosure(selectedEnclosures, bomModelsPC))
  } as ICountProjectDevices;
}

export const getTotalCountOfDevices = (bomModels: IBomModel[]): number => {
  return bomModels?.filter(model => isStatusActive(model?.article?.status))
    .reduce((sum, model) => {
      return sum += model.quantity;
    }, 0) ?? 0;
}


export const collateQuantitiesBom = (bomModels: IBomModel[], bomModelsAccessories: IBomModel[]) => {
  const accessories = shallowCloneObject(bomModelsAccessories);
  return shallowCloneObject(bomModels).filter(m => {
    const duplicate = accessories
      .find(acc => acc.article.eNumber === m.article.eNumber && acc.article.ref === m.article.ref);

    if (duplicate) {
      duplicate.quantity += m.quantity;
      return false;
    }

    return true;
  }).concat(accessories);
}

export const filterModelsBasedOnSelectedEnclosures = (value: IBomModel[], selectedEnclosures: number[]): IBomModel[] => {
  return mergeSimilarProducts(filterBySelectedEnclosure(selectedEnclosures, value));
}

export const filterBySelectedEnclosure = (selectedEnclosures: number[], models: IBomModel[]) => {
  if(!selectedEnclosures) {
    return models;
  }
  return models?.filter(m => selectedEnclosures?.indexOf(m?.article?.enclosureId) > -1) || [];
}

export const mergeSimilarProducts = (bomModels: IBomModel[]): IBomModel[] => {
  if(!bomModels) {
    return bomModels;
  }
  const models: IBomModel[] = shallowCloneObject(bomModels);
  const filteredBomModels: IBomModel[] = [];

  models?.forEach(model => {
    const foundInFiltered = isModelPresentInFilteredModels(filteredBomModels, model)
    if(foundInFiltered > -1) {
      filteredBomModels[foundInFiltered].quantity += model.quantity;
      prioritisePrewiringDeliveryOption(filteredBomModels[foundInFiltered], model);
    } else {
      filteredBomModels.push(model);
    }
  })
  return filteredBomModels;
}

const prioritisePrewiringDeliveryOption = (bomModel: IBomModel, newBomModel: IBomModel) => {
  if(bomModel?.article?.deliverToCustomer && newBomModel?.article?.deliverToPanelBuilder) {
    bomModel.article.deliverToPanelBuilder = true;
    bomModel.article.deliverToCustomer = false;
  }
}

const isModelPresentInFilteredModels = (filteredModels: IBomModel[], model: IBomModel): number => {
  return filteredModels.findIndex(m => m.article.ref === model.article.ref && m.article.eNumber === model.article.eNumber
    && m.article.productId === model.article.productId);
}

export const getAllModelsWithSimilarDetails = (modelsList: IBomModel[], modelDetail: IBomModel): IBomModel[] => {
  if(!modelDetail) {
    return;
  }
  return modelsList?.filter(m => m.article.ref === modelDetail.article.ref && m.article.eNumber === modelDetail.article.eNumber
    && m.article.productId === modelDetail.article.productId);
}


export const generateSelectedProductsObject = (selectedItems?: number[],
                                               selectedAccessories?: number[],
                                               selectedApartments?: number[],
                                               selectedComponentsIds?: number[]) => {
  if (selectedApartments && selectedAccessories && selectedItems) {
    return {
      productIds: selectedItems,
      accessoriesIds: selectedAccessories,
      apartmentIds: selectedApartments,
      componentsIds: selectedComponentsIds ?? undefined
    };
  }
}

export const shoppingCartHasTransferUnableOrPreWiringDevicesIncluded = (shoppingCart: IShoppingCart): boolean => {
  return shoppingCart.wholesalerUnableToTransferDevicesIncluded || shoppingCart.preWiringDevicesIncluded
}

export const createShoppingCart = (bomModels: IBomModel[],
                                   bomModelsAccessories: IBomModel[],
                                   selectedItems: number[],
                                   selectedItemsAccessories: number[]) => {
  return {
    wholesalerUnableToTransferDevicesIncluded: new WholesalerUnableToTransferDevicesExistPipe()
      .transform(bomModels, bomModelsAccessories, selectedItems, selectedItemsAccessories),
    preWiringDevicesIncluded: new PreWiringDevicesIncludedPipe()
      .transform(bomModels, bomModelsAccessories, selectedItems, selectedItemsAccessories),
    numberOfPositions: new NumberOfBomPositionsPipe()
      .transform(bomModels, bomModelsAccessories, selectedItems, selectedItemsAccessories)
  }
}

export const createProjectShoppingCart = (bomModels: IBomModel[],
                                          bomModelsAccessories: IBomModel[],
                                          selectedItems: number[],
                                          selectedItemsAccessories: number[],
                                          selectedEnclosures: number[],
                                          bomProjectAccessories: IBomModel[],
                                          selectedProjectAccessories: number[]) => {
  return {
    wholesalerUnableToTransferDevicesIncluded: new WholesalerUnableToTransferProjectDevicesExistPipe()
      .transform(bomModels,
        bomModelsAccessories,
        selectedItems,
        selectedItemsAccessories,
        bomProjectAccessories,
        selectedProjectAccessories,
        selectedEnclosures),
    preWiringDevicesIncluded: new PreWiringProjectDevicesIncludedPipe()
      .transform(bomModels,
        bomModelsAccessories,
        selectedItems,
        selectedItemsAccessories,
        bomProjectAccessories,
        selectedProjectAccessories,
        selectedEnclosures),
    numberOfPositions: new NumberOfProjectBomPositionsPipe()
      .transform(bomModels, bomModelsAccessories, selectedItems, selectedItemsAccessories,
        bomProjectAccessories,
        selectedProjectAccessories,
        selectedEnclosures)
  }
}

export const mapToEcommerceItems = (models: IBomModel[], projectId: number): IEcommerceBomItem[] => {
  return models.map(model => {
    return {
      itemId: model?.article?.ref,
      itemName: model?.article?.description,
      price: model?.article?.price,
      quantity: model?.quantity,
      configurationId: model?.article?.enclosureId,
      projectId
    };
  })
}

export const collateQuantitiesOnProjectBom = (baseModels: IBomModel[], additionalModels: IBomModel[]): IBomModel[] => {
  const models: IBomModel[] = additionalModels.filter(model => {
    const duplicate = baseModels
      .findIndex(base => base.article.eNumber === model.article.eNumber && base.article.ref === model.article.ref);
    if (duplicate > -1) {
      baseModels[duplicate].quantity += model.quantity;
      return false;
    }
    return true;
  })
  return baseModels.concat(models);
}

export const collateQuantitiesForEnclosureBom = (bomModelsAccessoriesEnclosure: IBomModel[],
                                  selectedItemsAccessories: number[],
                                  bomModels: IBomModel[],
                                  selectedItems: number[]) => {
  let bomModelsAccessories: IBomModel[] = shallowCloneObject(bomModelsAccessoriesEnclosure);
  bomModelsAccessories = bomModelsAccessories.filter(m => selectedItemsAccessories.includes(m.article.id));

  // @TODO we should consider replacing this block of code with collateQuantitiesBom function
  const models = bomModels.filter(m => {
    if (!selectedItems.includes(m.article.id)) {
      return false;
    }
    // tslint:disable-next-line: prefer-const
    let duplicate = bomModelsAccessories
      .find(acc => acc.article.eNumber === m.article.eNumber && acc.article.ref === m.article.ref);
    if (duplicate) {
      duplicate.quantity += m.quantity;
      return false;
    }
    return true;
  });

  return models.concat(bomModelsAccessories);
}

export const getBomGoogleAnalyticsFormat = (bomModels: IBomModel[],
                                            bomModelsAccessories: IBomModel[] = [],
                                            bomModelsProjectComponents: IBomModel[] = []): IBomAnalytics => {
  const copiedModels = collateQuantitiesOnProjectBom(collateQuantitiesOnProjectBom(bomModels, bomModelsAccessories),
    bomModelsProjectComponents);

  const priceWithoutVatBOMTable = sumOfProductsFromBOM(bomModels).toFixed(2);
  const priceWithoutVatAccessoriesTable = sumOfProductsFromBOM(bomModelsAccessories).toFixed(2);
  const priceWithoutVatProjectComponentsTable = sumOfProductsFromBOM(bomModelsProjectComponents).toFixed(2);

  const price = new CountTotalPriceBomPipe().transform(priceWithoutVatAccessoriesTable, priceWithoutVatBOMTable,
    priceWithoutVatProjectComponentsTable);

  return { copiedModels, price };
}

export const multiplyBomItemsQuantityByApartmentsNumber = (bomItems: IBomModel[], apartmentsNumber = 1) => {
  return bomItems?.map(item => {
    return {
      ...item,
      quantity: item?.quantity * apartmentsNumber,
    }
  })
}

export const getBomGoogleAnalyticsEnclosureBomFormat = (bomModels: IBomModel[],
                                                        bomModelsAccessories: IBomModel[],
                                                        apartmentsLength: number): IBomAnalytics => {
  const selectedItems = [];
  bomModels.forEach(bomModel => { addItem(bomModel, selectedItems); });

  const selectedItemsAccessories = [];
  bomModelsAccessories.forEach(model => addItem(model, selectedItemsAccessories));

  const copiedModels = multiplyBomItemsQuantityByApartmentsNumber(
    collateQuantitiesForEnclosureBom(bomModelsAccessories, selectedItemsAccessories, bomModels, selectedItems), apartmentsLength);

  const priceWithoutVatBOMTable = sumOfProductsFromBOM(bomModels, apartmentsLength).toFixed(2);
  const priceWithoutVatAccessoriesTable = sumOfProductsFromBOM(bomModelsAccessories, apartmentsLength).toFixed(2);

  const price = new CountTotalPriceBomPipe().transform(priceWithoutVatAccessoriesTable, priceWithoutVatBOMTable);

  return { copiedModels, price };
}
